<template>
  <div class="container mt-4">
    <div class="row col-16 d-flex justify-content-between align-items-center">
      <p class="display-4" style="font-size: 2.3em;">{{ createPageTitle() }}</p>

      <span>
        <p v-if="fullTimeSheet.completed" class="text-confirmed" style="color: #287c37;">BEVESTIGD</p>
        <p v-else class="text-confirmed" style="color: #a3101d;">NIET BEVESTIGD</p>
        <p class="text-month text-capitalize">{{ fullTimeSheet.yearMonth | toMonthNameWithYear }}</p>
      </span>
    </div>

    <div class="row col-16 mt-5">
      <div class="col-6" style="border-right: 1px solid lightGray;">
        <p class="display-4 text-secondary" style="font-size: 1.8em;">Gewerkte uren</p>
        <p class="display-4">{{ fullTimeSheet.workedHoursMinusAbsences | convertDotToCommaInNumber }}u</p>
      </div>
      <div class="col-6">
        <p class="display-4 text-secondary" style="font-size: 1.8em;">Verwachte uren</p>
        <p class="display-4">{{ fullTimeSheet.timeSheetHoursDto.needed }}u</p>
      </div>
    </div>

    <div class="row col-16 mt-5">
      <h4 class="mt-3">Uurtarieven</h4>
      <table class="table b-table-fixed">
        <thead class="thead-dark">
        <tr class="d-flex">
          <th scope="col" class="col-2"></th>
          <th scope="col" class="col-3">Tarief</th>
          <th scope="col" class="col-3">Start</th>
          <th scope="col" class="col-4">Einde</th>
        </tr>
        </thead>
        <tbody v-for="(rate, index) in selectedContract.orders" :key="index" :style=decideColorForTableRow(index)>
        <tr class="d-flex">
          <th scope="row" class="col-2">{{ index + 1 }}</th>
          <td class="col-3">{{ rate.baseHourlyRate }}</td>
          <td class="col-3">{{ rate.startDate }}</td>
          <td class="col-4">{{ rate.endDate }}</td>
        </tr>
        <tr v-if="rate.message != null" class="d-flex">
          <th scope="row" class="col-2 text-right">&#x27A5;</th>
          <td class="col-3 text-center">Referentie:</td>
          <td class="col-7 text-center break-text">{{ rate.message }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="row col-16 mt-5 justify-content-start">
      <h4 class="mt-3">Bonus Types</h4>
      <table class="table b-table-fixed">
        <thead class="thead-dark">
        <tr class="d-flex">
          <th scope="col" class="col-2"></th>
          <th scope="col" class="col-3">Type</th>
          <th scope="col" class="col-3">Percentage</th>
          <th scope="col" class="col-4">Actie</th>
        </tr>
        </thead>
        <BonusTypesPreviewComponent :bonusRates="sortedBonusRates()"/>
      </table>
    </div>
    <div class="row col-16 mt-5 justify-content-start">
      <h4 class="mt-3">Vrije Kosten</h4>
      <table class="table b-table-fixed">
        <thead class="thead-dark">
        <tr>
          <SortableTableHeaderComponent
              columnName="description" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Omschrijving"/>
          <SortableTableHeaderComponent
              columnName="amount" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Aantal"/>
          <SortableTableHeaderComponent
              columnName="units" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Eenheden"/>
          <SortableTableHeaderComponent
              columnName="cost" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Kost"/>
          <SortableTableHeaderComponent
              columnName="cost type" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Type"/>
          <SortableTableHeaderComponent
              columnName="total" :sortColumn="currentFreeCostSortColumn"
              :sortDirectionAscending="currentFreeCostSortDirectionAscending" :sortFunction="sortFreeCosts"
              text="Totaal"/>
          <th scope="col">Actie</th>
        </tr>
        </thead>
        <tbody>
        <template v-for="freeCostDto in sortedFreeCosts()">
          <EditableFreeCostComponent
              :key="freeCostDto" :freeCostDto="Object.assign({}, freeCostDto)"/>
        </template>
        </tbody>
      </table>
      <CreateFreeCostComponent v-show=createFreeCostVisible @FreeCostAdded="hideCreateFreeCost()" @FreeCostCanceled="hideCreateFreeCost"/>
      <div class="col-md-4 col-sm-12 text-center">
        <button v-show=!createFreeCostVisible class="btn-basic btn-fill-pulse justify-content-center" @click="toggleCreateFreeCost()">Vrije Kost Toevoegen</button>
      </div>
    </div>

    <div class="row justify-content-start mt-3">
      <button class="btn-basic btn-fill-pulse mr-3" style="width: 180px;" @click="createNewInvoice()">Genereer factuur
      </button>
      <button
          :disabled="fullTimeSheet.invoiced" style="width: 180px;" class="btn-basic btn-outline-pulse-dark"
          @click="openTimesheet()">
        Pas timesheet aan
      </button>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {sortListByColumnAndDirection} from "@/util/SortingUtil";
import SortableTableHeaderComponent from "@/components/tables/SortableTableHeader";
import CreateFreeCostComponent from "@/components/invoicing/CreateFreeCostComponent";
import BonusTypesPreviewComponent from "@/Admin/BonusRateComponent/BonusTypesPreviewComponent";
import EditableFreeCostComponent from "@/components/invoicing/EditableFreeCostComponent.vue";

export default {
  name: "AdminViewInvoicePreview",
  components: {
    SortableTableHeaderComponent,
    EditableFreeCostComponent,
    CreateFreeCostComponent,
    BonusTypesPreviewComponent
  },
  data: () => ({
    currentSortColumn: "dateFormatted",
    currentSortDirectionAscending: true,
    currentBonusRatesSortDirectionAscending: true,
    currentBonusRateSortColumn: "bonusType",
    currentFreeCostSortDirectionAscending: true,
    currentFreeCostSortColumn: "description",
    createFreeCostVisible: false,
    workedHoursMinusAbsences: 0
  }),
  computed: {
    ...mapGetters({
      selectedCustomerDetail: "administrationCustomer/selectedCustomerDetail",
      fullTimeSheet: "administrationTimeSheet/fullTimeSheet",
      selectedTimeSheetInfo: "administrationTimeSheet/selectedTimeSheetInfo",
      freeCosts: "administrationTimeSheet/freeCosts",
      customerId : "administrationTimeSheet/customerId",
      selectedContract: "administrationContract/selectedContract"
    }),
  },
  created() {
    this.fetchSelectedContractById(this.selectedTimeSheetInfo.contractId);
    this.fetchFullTimeSheet();
    this.fetchCustomerByLink(this.customerId);
  },
  methods: {
    ...mapActions("administrationContract", ["fetchSelectedContractById"]),
    ...mapActions("administrationInvoice", ["calculateInvoice", "fetchAccountingCostsList"]),
    ...mapActions("administrationTimeSheet", ["fetchFullTimeSheet", "fetchFreeCosts"]),
    ...mapActions("administrationCustomer", ["fetchCustomerByLink"]),

    toggleCreateFreeCost: function () {
      this.createFreeCostVisible = !this.createFreeCostVisible;
    },
    hideCreateFreeCost: function () {
      this.createFreeCostVisible = false;
    },
    sortedBonusRates: function () {
      const bonusRates = [...this.selectedCustomerDetail.bonusRateDtos || []];
      return sortListByColumnAndDirection(bonusRates, this.currentBonusRateSortColumn, this.currentBonusRatesSortDirectionAscending);
    },
    created() {
      this.fetchFreeCosts();
      this.fetchAccountingCostsList();
    },
    openInvoice() {
      if (this.fullTimeSheet.invoiced) {
        this.$router.push({name: "adminViewInvoice"});
      } else {
        if (confirm("Ben je zeker dat je een factuur wil genereren? Eenmaal de factuur is opgesteld kan je geen shifts meer toevoegen.")) {
          this.$router.push({name: "adminViewInvoice"});
        }
      }
    },
    createNewInvoice() {
      this.calculateInvoice(this.selectedTimeSheetInfo);
      this.$router.push({name: "adminViewInvoice"});
    },
    createPageTitle() {
      return this.selectedTimeSheetInfo.username + " - " + this.selectedTimeSheetInfo.customerName;
    },
    openTimesheet() {
      this.$router.push({name: "adminDetails"});
    },
    sort: function (newSortColumn) {
      if (newSortColumn === this.currentSortColumn) {
        this.currentSortDirectionAscending = !this.currentSortDirectionAscending;
      } else {
        this.currentSortDirectionAscending = true;
      }
      this.currentSortColumn = newSortColumn;
    },
    sortBonusRates: function (newSortColumn) {
      if (newSortColumn === this.currentBonusRateSortColumn) {
        this.currentBonusRatesSortDirectionAscending = !this.currentBonusRatesSortDirectionAscending;
      } else {
        this.currentBonusRatesSortDirectionAscending = true;
      }
      this.currentBonusRateSortColumn = newSortColumn;
    },
    sortedFreeCosts: function () {
      const freeCosts = [...this.freeCosts || []];
      return sortListByColumnAndDirection(freeCosts, this.currentFreeCostSortColumn, this.currentFreeCostSortDirectionAscending);
    },
    sortFreeCosts: function (newSortColumn) {
      if (newSortColumn === this.currentFreeCostSortColumn) {
        this.currentFreeCostSortDirectionAscending = !this.currentFreeCostSortDirectionAscending;
      } else {
        this.currentFreeCostSortDirectionAscending = true;
      }
      this.currentFreeCostSortColumn = newSortColumn;
    },
    decideColorForTableRow(index) {
      if (index % 2 == 0) {
        return "background-color: white";
      } else {
        return "background-color: lightgrey";
      }
    }
  }
};
</script>

<style scoped>
.text-confirmed {
  font-weight: bold;
  font-size: 1.6em;
  margin: 0;
}

.text-month {
  font-weight: 100;
  font-size: 1.4em;
  color: gray;
}

.table > tbody > tr > td,
.table > tbody > tr > th {
  border-top: none;
}

.break-text {
  word-wrap: break-word;
}

</style>